import { ref, watch, onUnmounted, computed } from 'vue';
import {
  mapDataToWidget,
  buildEndpointUrl,
  API_ENDPOINTS,
} from '../utils/apiConnectors';
import axios from 'axios';
import Auth from 'dashboard/api/auth';

export const useApiData = (widget, accountId, refreshInterval = 30000) => {
  const data = ref(null);
  const loading = ref(false);
  const error = ref(null);
  let intervalId = null;

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (Auth.hasAuthCookie()) {
      const authData = Auth.getAuthData();
      if (authData && authData['access-token']) {
        headers['access-token'] = authData['access-token'];
        headers['token-type'] = authData['token-type'];
        headers.client = authData.client;
        headers.expiry = authData.expiry;
        headers.uid = authData.uid;
      }
    }

    return headers;
  };

  const fetchData = async () => {
    const connector = widget.value?.props?.apiConnector;
    if (!connector) {
      data.value = null;
      return;
    }

    // Extraire la valeur de accountId (peut être un computed, une valeur directe, etc.)
    let accountIdValue;
    if (typeof accountId === 'function') {
      accountIdValue = accountId();
    } else if (
      accountId &&
      typeof accountId === 'object' &&
      'value' in accountId
    ) {
      accountIdValue = accountId.value;
    } else {
      accountIdValue = accountId;
    }

    if (!accountIdValue) {
      data.value = null;
      error.value = 'Account ID non disponible';
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const endpointConfig = API_ENDPOINTS.find(
        e => e.id === connector.endpointId
      ) || {
        endpoint: connector.endpoint,
        method: connector.method || 'GET',
        dataMapper: {},
      };

      if (!endpointConfig.endpoint) {
        throw new Error('Endpoint non configuré');
      }

      const url = buildEndpointUrl(
        endpointConfig.endpoint,
        accountIdValue,
        connector.params || {}
      );

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      const rawData = response.data;

      // Mapper les données selon le type de widget
      let mappedData = rawData;
      if (
        endpointConfig.dataMapper &&
        endpointConfig.dataMapper[widget.value.type]
      ) {
        mappedData = mapDataToWidget(
          rawData,
          endpointConfig.dataMapper,
          widget.value.type
        );
      }

      data.value = {
        raw: rawData,
        mapped: mappedData,
        timestamp: Date.now(),
      };

      error.value = null;
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la récupération des données';
      error.value = errorMessage;
      console.error('Erreur API:', err);
      data.value = null;
    } finally {
      loading.value = false;
    }
  };

  const startAutoRefresh = () => {
    if (intervalId) return;
    if (refreshInterval > 0) {
      intervalId = setInterval(fetchData, refreshInterval);
    }
  };

  const stopAutoRefresh = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  // Extraire la valeur de accountId pour le watch
  const getAccountIdValue = () => {
    if (typeof accountId === 'function') {
      return accountId();
    }
    if (accountId && typeof accountId === 'object' && 'value' in accountId) {
      return accountId.value;
    }
    return accountId;
  };

  // Watch pour récupérer les données quand le widget change
  watch(
    () => widget.value?.props?.apiConnector,
    (newConnector, oldConnector) => {
      // Ne fetch que si le connecteur a vraiment changé
      if (JSON.stringify(newConnector) !== JSON.stringify(oldConnector)) {
        fetchData();
        if (refreshInterval > 0) {
          stopAutoRefresh();
          startAutoRefresh();
        }
      }
    },
    { immediate: true, deep: true }
  );

  // Watch pour recharger les données quand l'accountId devient disponible
  watch(
    () => getAccountIdValue(),
    (newAccountId, oldAccountId) => {
      // Recharger si l'accountId passe de null/undefined à une valeur valide
      if (!oldAccountId && newAccountId) {
        fetchData();
        if (refreshInterval > 0) {
          stopAutoRefresh();
          startAutoRefresh();
        }
      }
    },
    { immediate: true }
  );

  // Nettoyer l'interval au démontage
  onUnmounted(() => {
    stopAutoRefresh();
  });

  return {
    data,
    loading,
    error,
    fetchData,
    startAutoRefresh,
    stopAutoRefresh,
  };
};
